/**
 * Helper functions for Email Ad Validation Data Hook
 * Extracted to reduce code duplication
 */
import { formatDateToDDMMYY } from '../../../constants/utils';

/**
 * Transform a single API run object to local format
 * @param run
 */
export const transformRunData = (run) => ({
  ...run,
  runName: run.run_name || run.runName || 'Unnamed Run',
  submitDate: new Date(run.created_at || run.submit_date || Date.now()),
  fromEmail: run.from_email || run.fromEmail || '',
  toEmail: run.to_email || run.toEmail || '',
  emailType: run.email_type || run.emailType || '',
  offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
  startDate: new Date(run.start_date || run.startDate || Date.now()),
  endDate: new Date(run.end_date || run.endDate || Date.now()),
  status: run.status || 'QUEUED',
  subject: run.subject || '',
  dateRange:
    run.start_date && run.end_date
      ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
      : 'No date range',
});

/**
 * Extract runs array from API response
 * @param data
 */
export const extractRunsFromResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  if (Array.isArray(data.results)) {
    return data.results;
  }

  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

/**
 * Extract total count from API response
 * @param data
 * @param runsLength
 */
export const extractTotalCount = (data, runsLength) => {
  if (!data || typeof data !== 'object') {
    return 0;
  }

  return data.count || data.total || runsLength;
};

/**
 * Build filter parameters for API call
 * @param filters
 * @param page
 */
export const buildFilterParams = (filters, page = 1) => {
  const params = { page };

  if (filters.status) {
    params.status = filters.status;
  }

  if (filters.emailType) {
    params.emailType = filters.emailType;
  }

  return params;
};

/**
 * Process API response and return transformed data
 * @param data
 */
export const processApiResponse = (data) => {
  const runs = extractRunsFromResponse(data);
  const total = extractTotalCount(data, runs.length);
  const transformedData = runs.map(transformRunData);

  return {
    runs: transformedData,
    total,
  };
};

/**
 * Helper function to safely handle date inputs from DatePickerMui
 * @param dateInput
 */
export const createSafeDate = (dateInput) => {
  if (!dateInput) {
    return new Date();
  }

  // If it's already a Date object, return it
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? new Date() : dateInput;
  }

  // If it's a string in dd/MM/yy format, parse it correctly
  if (typeof dateInput === 'string' && dateInput.includes('/')) {
    const parts = dateInput.split('/');

    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
      const year = parseInt(parts[2], 10);

      // Assume 2000s for 2-digit years
      const fullYear = year < 100 ? 2000 + year : year;
      const parsedDate = new Date(fullYear, month, day);

      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }
  }

  // Try standard Date parsing as fallback
  const parsedDate = new Date(dateInput);

  return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};
